<template>
  <div ref="containerRef" class="tag-badges tag-badges--grid-singleline">
    <span
      v-for="(tag, idx) in displayedTags"
      :key="`grid-tag-visible-${idx}`"
      class="badge badge-light mb-0"
      :title="tagDetailMap[idx] && tagDetailMap[idx].category_name || ''"
    >
      {{ tag }}
    </span>
    <span
      v-if="hiddenCount > 0"
      class="badge badge-light badge-tag-overflow mb-0"
      :title="hiddenTagsTooltip"
      tabindex="0"
    >
      +{{ hiddenCount }}
    </span>
  </div>

  <div ref="measureRef" class="tag-badges tag-badges--measure" aria-hidden="true">
    <span
      v-for="(tag, idx) in normalizedTags"
      :key="`grid-tag-measure-${idx}`"
      class="badge badge-light mb-0"
      data-tag-measure
    >
      {{ tag }}
    </span>
    <span ref="measureOverflowRef" class="badge badge-light badge-tag-overflow mb-0"></span>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
  tags: {
    type: Array,
    default: () => [],
  },
  tagDetails: {
    type: Array,
    default: () => [],
  },
});

const containerRef = ref(null);
const measureRef = ref(null);
const measureOverflowRef = ref(null);
const visibleCount = ref(null);

let resizeObserver = null;
let rafId = null;

const normalizedTags = computed(() => {
  const source = props.tagDetails.length > 0
    ? props.tagDetails.map((d) => (d ? d.text : null))
    : props.tags;
  if (!Array.isArray(source)) {
    return [];
  }

  return source
    .map((tag) => (tag === null || tag === undefined ? '' : String(tag)))
    .filter((tag) => tag !== '');
});

const tagDetailMap = computed(() => {
  if (!props.tagDetails || props.tagDetails.length === 0) return [];
  return props.tagDetails.filter((d) => d && d.text);
});

const displayedTags = computed(() => {
  const tags = normalizedTags.value;
  if (visibleCount.value === null) {
    return tags;
  }

  return tags.slice(0, Math.max(0, visibleCount.value));
});

const hiddenTags = computed(() => {
  const tags = normalizedTags.value;
  const visible =
    visibleCount.value === null ? tags.length : Math.max(0, visibleCount.value);

  return tags.slice(visible);
});

const hiddenCount = computed(() => {
  return hiddenTags.value.length;
});

const hiddenTagsTooltip = computed(() => hiddenTags.value.join(', '));

const getGapPx = () => {
  if (!measureRef.value) {
    return 0;
  }

  const style = window.getComputedStyle(measureRef.value);
  const raw = style.columnGap || style.gap || '0';
  const parsed = Number.parseFloat(raw);

  return Number.isFinite(parsed) ? parsed : 0;
};

const getOverflowWidth = (count, cache) => {
  if (count <= 0 || !measureOverflowRef.value) {
    return 0;
  }

  if (cache[count] !== undefined) {
    return cache[count];
  }

  measureOverflowRef.value.textContent = `+${count}`;
  const width = measureOverflowRef.value.getBoundingClientRect().width;
  cache[count] = Math.ceil(width);

  return cache[count];
};

const recalcVisibleTags = () => {
  const tags = normalizedTags.value;
  if (tags.length === 0) {
    visibleCount.value = 0;
    return;
  }

  const containerWidth = Math.floor(containerRef.value?.clientWidth || 0);
  if (containerWidth <= 0 || !measureRef.value) {
    visibleCount.value = tags.length;
    return;
  }

  const badgeElements = Array.from(
    measureRef.value.querySelectorAll('[data-tag-measure]')
  );
  if (badgeElements.length === 0) {
    visibleCount.value = 0;
    return;
  }

  const badgeWidths = badgeElements.map((el) =>
    Math.ceil(el.getBoundingClientRect().width)
  );
  const gap = getGapPx();
  const overflowWidthCache = {};

  let used = 0;
  let fitCount = 0;

  for (let i = 0; i < badgeWidths.length; i += 1) {
    const currentWidth = badgeWidths[i];
    const nextUsed = fitCount === 0 ? currentWidth : used + gap + currentWidth;
    const remain = badgeWidths.length - (i + 1);
    let required = nextUsed;

    if (remain > 0) {
      required += gap + getOverflowWidth(remain, overflowWidthCache);
    }

    if (required <= containerWidth) {
      fitCount = i + 1;
      used = nextUsed;
      continue;
    }

    break;
  }

  visibleCount.value = fitCount;
};

const scheduleRecalc = () => {
  if (rafId !== null) {
    window.cancelAnimationFrame(rafId);
  }

  rafId = window.requestAnimationFrame(() => {
    rafId = null;
    recalcVisibleTags();
  });
};

watch(
  normalizedTags,
  () => {
    nextTick(() => {
      scheduleRecalc();
    });
  },
  { immediate: true }
);

onMounted(() => {
  scheduleRecalc();

  if (window.ResizeObserver && containerRef.value) {
    resizeObserver = new ResizeObserver(() => {
      scheduleRecalc();
    });
    resizeObserver.observe(containerRef.value);
  }

  window.addEventListener('resize', scheduleRecalc);
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  if (rafId !== null) {
    window.cancelAnimationFrame(rafId);
    rafId = null;
  }

  window.removeEventListener('resize', scheduleRecalc);
});
</script>
